/* eslint-disable import/max-dependencies */
// @flow
import React from "react";
import type { StatelessFunctionalComponent, Element } from "react";
import { Box, Link } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import {
  ViewLink,
  Table,
  ShowMoreItems,
  IsActiveLabel,
} from "@fas/ui-core";
import { useDispatch } from "react-redux";
import { useTable } from "@fas/ui-framework/lib/services/table";
import type { TableProps } from "@fas/ui-framework/lib/services/table";
import type { Column } from "@fas/ui-core/lib/Table/Table.types";
import { SelectSearchComponent } from "@fas/ui-core/lib/SearchComponents";
import type { SearchComponentProps } from "@fas/ui-core/lib/SearchComponents";
import { getIPPListSaga } from "../../actions/ippList";
import type { IPPListData, Classes } from "./types/IppList.types";
import { IPP_LIST_TABLE } from "../../helpers/constants";
import Collapse from "../Collapse";
import externalRedirect from "../App/ExternalRedirect";
import { compareById, getFields } from "../../services/utils";
import MotivationTemplatesColumn from "../MotivationTemplatesColumn";

type Props = {|
  Actions?: Element<*>,
  choicedData?: Array<IPPListData>,
  onSelect?: (item: IPPListData) => mixed,
  onDeselect?: (item: IPPListData) => mixed,
  onCreate?: () => mixed,
  rowSelectAvailable?: () => boolean,
  allSelectAvailable?: () => boolean,
|};

const useStyles: () => Classes = makeStyles((): {[$Keys<Classes>]: *} => ({
  fixedSizeColumn: {
    width: "20%",
  },
}));

const handleCreate: () => null = () => externalRedirect({ to: "/ipp/create", target: "_self" });

const IPPList: StatelessFunctionalComponent<Props> = ({
  Actions,
  choicedData,
  onSelect,
  onDeselect,
  onCreate = handleCreate,
  rowSelectAvailable = (): boolean => true,
  allSelectAvailable = (): boolean => true,
}) => {
  const dispatch: <A>(A) => A = useDispatch();
  const { fixedSizeColumn }: Classes = useStyles();
  const tableProps: TableProps = useTable(IPP_LIST_TABLE);
  const handleLoad = () => dispatch(getIPPListSaga());

  const columns: Array<Column<IPPListData>> = [
    {
      field: "isActive",
      label: "Status",
      sortable: false,
      searchable: true,
      SearchComponent: (props: SearchComponentProps): Element<*> => (
        <SelectSearchComponent
          {...props}
          label="Select"
          values={[
            { value: "", title: "None" },
            { value: "true", title: "Active" },
            { value: "false", title: "Inactive" },
          ]}
        />
      ),
      render: IsActiveLabel,
    },
    {
      field: "id",
      label: "Id",
      searchable: false,
      sortable: false,
      className: fixedSizeColumn,
    },
    {
      label: "Name",
      field: "name",
      searchable: true,
      sortable: false,
      className: fixedSizeColumn,
      render: (row: IPPListData): Element<Link> => (
        <ViewLink id={row.id} name={row.name} viewPath="/ipp/view/:id" />
      ),
    },
    {
      field: "behaviorToAction",
      label: "Behavior To Action",
      searchable: false,
      sortable: false,
      className: fixedSizeColumn,
    },
    {
      field: "motivationTemplates",
      label: "Motivation Templates",
      className: fixedSizeColumn,
      render: MotivationTemplatesColumn,
    },
    {
      field: "BrowserLanguages",
      label: "Browser Languages",
      searchable: true,
      sortable: false,
      className: fixedSizeColumn,
      render: (row: IPPListData): Element<typeof Collapse> => (
        <ShowMoreItems data={getFields(row.BrowserLanguages, "language")} />
      ),
    },
  ];

  return (
    <Box m={2}>
      <Table
        {...tableProps}
        title="IPP Motivation content"
        columns={columns}
        onLoad={handleLoad}
        onCreate={onCreate}
        selected={choicedData || tableProps.selected}
        onSelect={onSelect || tableProps.onSelect}
        onDeselect={onDeselect || tableProps.onDeselect}
        rowSelectAvailable={rowSelectAvailable}
        allSelectAvailable={allSelectAvailable}
        compareRows={compareById}
        Actions={Actions}
      />
    </Box>
  );
};

export default IPPList;
